import { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Plus, Trash2 } from 'lucide-react'
import type { ContentItem } from '../../api/contentApi'

/* eslint-disable @typescript-eslint/no-explicit-any */
type Payload = Record<string, any>

interface ArtworkEditorProps {
  item: ContentItem
  onSave: (payload: Payload) => void
  saving?: boolean
}

export function ArtworkEditor({ item, onSave, saving }: ArtworkEditorProps) {
  const [p, setP] = useState<Payload>((item.payload as Payload) || {})
  useEffect(() => {
    setP((item.payload as Payload) || {})
  }, [item.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k: string, v: any) => setP((prev) => ({ ...prev, [k]: v }))
  const concepts: Payload[] = Array.isArray(p.art_concepts) ? p.art_concepts : []
  const setConcepts = (next: Payload[]) => set('art_concepts', next)
  const updateConcept = (i: number, k: string, v: any) =>
    setConcepts(concepts.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)))
  const addConcept = () => setConcepts([...concepts, { label: '', category: 'Subject', description: '', picasso_query: '' }])
  const removeConcept = (i: number) => setConcepts(concepts.filter((_, idx) => idx !== i))

  return (
    <div className="space-y-5">
      <Field label="Headline">
        <Input value={String(p.headline || '')} onChange={(e) => set('headline', e.target.value)} />
      </Field>
      <Field label="Editorial description">
        <Textarea rows={8} value={String(p.editorial_description || '')} onChange={(e) => set('editorial_description', e.target.value)} />
      </Field>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>Concept cards</Label>
          <Button variant="outline" size="sm" onClick={addConcept}>
            <Plus className="h-4 w-4 mr-1" />
            Add card
          </Button>
        </div>
        {concepts.map((c, i) => (
          <div key={i} className="rounded-md border p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Label"><Input value={String(c.label || '')} onChange={(e) => updateConcept(i, 'label', e.target.value)} /></Field>
              <Field label="Category">
                <Input
                  value={String(c.category || '')}
                  onChange={(e) => updateConcept(i, 'category', e.target.value)}
                  placeholder="Subject | Style | Mood | Technique | Movement"
                />
              </Field>
            </div>
            <Field label="Description"><Textarea rows={2} value={String(c.description || '')} onChange={(e) => updateConcept(i, 'description', e.target.value)} /></Field>
            <Field label="Concierge query (deep-link seed)"><Input value={String(c.picasso_query || '')} onChange={(e) => updateConcept(i, 'picasso_query', e.target.value)} /></Field>
            <div className="flex items-center">
              {c.related_count != null && (
                <span className="text-xs text-muted-foreground">
                  {c.related_count} related works{c.collection ? ' · linked collection' : ''}
                </span>
              )}
              <Button variant="ghost" size="sm" className="text-red-600 ml-auto" onClick={() => removeConcept(i)}>
                <Trash2 className="h-4 w-4 mr-1" />
                Remove
              </Button>
            </div>
          </div>
        ))}
        {concepts.length === 0 && <p className="text-sm text-muted-foreground">No concept cards yet — add one.</p>}
      </div>

      <div className="flex justify-end">
        <Button onClick={() => onSave(p)} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  )
}
